/**
 * WHAT EACH SCREEN NEEDS, AND THE THREE STATES IT CAN BE IN.
 *
 * Loading, loaded, and FAILED — never "loaded, empty". A back office that shows
 * a zero when the server did not answer has told a manager their venue took
 * nothing, which is the one number they must be able to trust. So every screen
 * carries its own failure and says so on the screen, with the reason.
 *
 * Plain TypeScript with no DOM, so the shapes and the date arithmetic are
 * testable without mounting anything — the same split the till keeps.
 */

import type {
  CheckDetail,
  CheckSummary,
  ConsoleApi,
  FiscalState,
  KitchenTimings,
  Menu,
  SalesReport,
  ShiftPage,
} from "./api";
import { ApiError } from "./api";
import { daysBefore } from "./format";

export type Loaded<T> =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly value: T }
  | { readonly status: "failed"; readonly kind: string; readonly detail: string };

export const loading = <T>(): Loaded<T> => ({ status: "loading" });

/**
 * Run a request into a `Loaded`.
 *
 * Catches only `ApiError`. Anything else is a bug in this app rather than a
 * fact about the server, and swallowing it would turn a crash into a shrug.
 */
export async function attempt<T>(run: () => Promise<T>): Promise<Loaded<T>> {
  try {
    return { status: "ready", value: await run() };
  } catch (error) {
    if (error instanceof ApiError) {
      return { status: "failed", kind: error.kind, detail: error.message };
    }
    throw error;
  }
}

/** The ranges the period picker offers, as business dates. */
export type RangeName = "today" | "week" | "month" | "custom";

export interface Range {
  readonly from: string;
  readonly to: string;
}

/** The longest period the reports answer for (the API's own limit). */
const MAX_DAYS = 90;

/**
 * A period somebody typed, made sensible rather than refused: in order, not
 * past today, at most 90 days (the latest kept), and a half-typed date is
 * taken as the other one.
 */
export function customRange(from: string, to: string, today: string): Range {
  const valid = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
  let a = valid(from) ? from : valid(to) ? to : today;
  let b = valid(to) ? to : a;
  if (a > b) [a, b] = [b, a];
  if (b > today) b = today;
  if (a > b) a = b;
  const earliest = daysBefore(b, MAX_DAYS - 1);
  return { from: a < earliest ? earliest : a, to: b };
}

export function rangeOf(name: RangeName, today: string, custom?: Range): Range {
  switch (name) {
    case "custom":
      return custom ? customRange(custom.from, custom.to, today) : { from: today, to: today };
    case "today":
      return { from: today, to: today };
    case "week":
      // Seven days INCLUDING today, which is what "last 7 days" means to
      // everybody except an off-by-one.
      return { from: daysBefore(today, 6), to: today };
    case "month":
      return { from: daysBefore(today, 29), to: today };
  }
}

// --- Per-screen loaders ----------------------------------------------------

export const loadSales = (api: ConsoleApi, range: Range): Promise<Loaded<SalesReport>> =>
  attempt(() => api.sales(range.from, range.to));

export const loadChecks = (api: ConsoleApi, range: Range): Promise<Loaded<CheckSummary[]>> =>
  attempt(() => api.checks(range.from, range.to));

export const loadCheck = (api: ConsoleApi, orderId: string): Promise<Loaded<CheckDetail>> =>
  attempt(() => api.check(orderId));

export const loadShifts = (api: ConsoleApi, range: Range): Promise<Loaded<ShiftPage>> =>
  attempt(() => api.shifts(range.from, range.to));

export const loadKitchen = (api: ConsoleApi, range: Range): Promise<Loaded<KitchenTimings>> =>
  attempt(() => api.kitchenTimings(range.from, range.to));

export const loadFiscal = (api: ConsoleApi): Promise<Loaded<FiscalState>> =>
  attempt(() => api.fiscal());


// --- Derived facts the screens need ----------------------------------------

export interface TodayFacts {
  readonly sales: SalesReport;
  readonly checks: readonly CheckSummary[];
  readonly fiscal: FiscalState;
}

/**
 * The open tables and what is sitting on them.
 *
 * Separate from the day's takings on purpose: an open check is NOT revenue —
 * the guests have been served and have not paid, so counting it would book the
 * money today and again when it settles.
 */
export function openChecks(checks: readonly CheckSummary[]): {
  readonly count: number;
  readonly onTablesMinor: bigint;
} {
  const open = checks.filter((c) => c.state === "OPEN");
  return {
    count: open.length,
    onTablesMinor: open.reduce((acc, c) => acc + c.dueMinor, 0n),
  };
}

/**
 * The share each payment form took, in basis points.
 *
 * Basis points rather than a percentage float, for the same reason money is
 * minor units: the moment a share becomes a float somebody formats it with
 * `.toFixed` and the build stops.
 */
export function tenderShares(
  tenders: readonly { kind: string; amountMinor: bigint }[],
): readonly { readonly kind: string; readonly amountMinor: bigint; readonly shareBp: number }[] {
  const total = tenders.reduce((acc, t) => acc + t.amountMinor, 0n);
  if (total === 0n) return tenders.map((t) => ({ ...t, shareBp: 0 }));
  return tenders
    .map((t) => ({
      kind: t.kind,
      amountMinor: t.amountMinor,
      shareBp: Number((t.amountMinor * 10_000n) / total),
    }))
    .sort((a, b) => b.shareBp - a.shareBp);
}
